import React from "react";

import { isEmpty } from "lodash-es";
import { useForm } from "react-hook-form";

import {
  taktileInternalPrefix,
  validateNotStartsWithInternalPrefix,
} from "src/api/constants";
import { useUpdateWorkspaceApiKey } from "src/apiKeys/api/queries";
import { ExternalLink } from "src/base-components/ExternalLink";
import { Input } from "src/base-components/Input";
import { WorkspaceServiceAccount } from "src/clients/taktile-api";
import { DOCS_API_KEYS } from "src/constants/ExternalLinks";
import { Button } from "src/design-system/Button";
import { ErrorHint } from "src/design-system/ErrorHint";
import { Modal } from "src/design-system/Modal";
import { logger } from "src/utils/logger";

type ApiKeyInputs = {
  name: string;
};

type Props = {
  keyToEdit?: WorkspaceServiceAccount;
  open: boolean;
  organizationId: string;
  workspaceId: string;
  onClose: () => void;
  afterLeave: () => void;
};

export const EditApiKeyModal: React.FC<Props> = ({
  keyToEdit,
  open,
  organizationId: orgId,
  workspaceId: wsId,
  onClose,
  afterLeave,
}) => {
  const updateWorkspaceApiKey = useUpdateWorkspaceApiKey(
    orgId,
    wsId,
    keyToEdit?.id ?? "",
  );

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting, isValid, isDirty },
  } = useForm<ApiKeyInputs>({
    mode: "onChange",
    values: keyToEdit
      ? {
          name: keyToEdit.name,
        }
      : undefined,
  });

  const onConfirm = handleSubmit(async (input: ApiKeyInputs) => {
    if (!keyToEdit) {
      return;
    }

    try {
      await updateWorkspaceApiKey.mutateAsync(input);
    } catch (error) {
      logger.error(`Error when updating ApiKey: ${error}`);
    }
    onClose();
  });

  return (
    <Modal afterLeave={afterLeave} open={open} onClose={onClose}>
      <Modal.Header
        description={
          <>
            Read more about API keys on our{" "}
            <ExternalLink href={DOCS_API_KEYS}>Documentation</ExternalLink>{" "}
            page.
          </>
        }
      >
        API key
      </Modal.Header>
      <form onSubmit={onConfirm}>
        <Modal.Content>
          <Input
            {...register("name", {
              required: true,
              validate: validateNotStartsWithInternalPrefix(
                `API key name cannot start with ${taktileInternalPrefix}`,
              ),
            })}
            errored={Boolean(errors.name?.message)}
            placeholder="Name your API key"
            fullWidth
          />
          {Boolean(errors.name?.message) && (
            <ErrorHint>{errors.name?.message}</ErrorHint>
          )}
        </Modal.Content>
        <Modal.Footer
          primaryButton={
            <Button
              isDisabled={
                !isEmpty(errors) || !isValid || isSubmitting || !isDirty
              }
              htmlType="submit"
              isLoading={isSubmitting}
              variant="primary"
            >
              Save key
            </Button>
          }
        />
      </form>
    </Modal>
  );
};
